/**
 * A tela do fluxo: paleta · canvas · inspector.
 *
 * Três colunas, e o fluxo de leitura é o da tarefa: **de onde tirar** um bloco, **onde ele fica**,
 * **o que ele faz**. É o mesmo arranjo do n8n, do ManyChat e de qualquer editor de nó, e é a razão
 * de não ter mudado — o que mudou foi o que sai da coluna da direita, que agora mostra só o bloco
 * selecionado (ver `Inspector`).
 *
 * O preview não vive aqui. Ele tem tela própria (`pages/Preview.tsx`): o widget precisa de uns 400px
 * de largura e a coluna lateral tem 384px, então ele aparecia cortado — e três painéis mais o canvas
 * deixavam a conversa difícil de acompanhar.
 */

import { useCallback, useMemo, useRef } from 'react';
import {
  Background,
  ConnectionLineType,
  Controls,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  type NodeMouseHandler,
} from '@xyflow/react';
import type { BlockKind } from '@merlin/flow-engine';
import { BlockNode } from '../components/BlockNode.js';
import { Inspector } from '../components/inspector/Inspector.js';
import { BLOCK_DRAG_TYPE, Palette } from '../components/Palette.js';
import { useEditor } from '../store/editor.js';
import { BranchEdge } from '../flow/BranchEdge.js';
import {
  buildConnectionIndex,
  decorateEdges,
  reachableNodeIds,
  type AnyBlockNode,
} from '../flow/mapping.js';

const nodeTypes = {
  message: BlockNode,
  input: BlockNode,
  buttons: BlockNode,
  condition: BlockNode,
  browserCode: BlockNode,
  browserWebhook: BlockNode,
  backendWebhook: BlockNode,
};

const edgeTypes = { branch: BranchEdge };

function Canvas() {
  const nodes = useEditor((state) => state.nodes);
  const edges = useEditor((state) => state.edges);
  const startNodeId = useEditor((state) => state.startNodeId);
  const onNodesChange = useEditor((state) => state.onNodesChange);
  const onEdgesChange = useEditor((state) => state.onEdgesChange);
  const onConnect = useEditor((state) => state.onConnect);
  const select = useEditor((state) => state.select);

  /*
   * `isStart` e `unreachable` são adorno de renderização: `fromReactFlow` persiste apenas
   * `data.block`, então marcá-los aqui não contamina o documento salvo.
   */
  const decoratedNodes = useMemo(() => {
    const reachable = reachableNodeIds(nodes, edges, startNodeId);
    const connections = buildConnectionIndex(edges);
    return nodes.map((node) => ({
      ...node,
      data: {
        ...node.data,
        isStart: node.id === startNodeId,
        unreachable: !reachable.has(node.id),
        // Faz o conector aparecer vazado quando nada está ligado nele — a saída livre se destaca.
        connections: connections.get(node.id) ?? { connectedSourceHandles: [], hasIncoming: false },
      },
    }));
  }, [nodes, edges, startNodeId]);

  // Cor, seta e nome do ramo vêm da porta de origem, recalculados a cada render para acompanharem
  // a renomeação de um botão.
  const decoratedEdges = useMemo(() => decorateEdges(edges, nodes), [edges, nodes]);

  const onNodeClick: NodeMouseHandler<AnyBlockNode> = useCallback(
    (_event, node) => select(node.id),
    [select],
  );

  return (
    <ReactFlow
      nodes={decoratedNodes}
      edges={decoratedEdges}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onConnect={onConnect}
      onNodeClick={onNodeClick}
      onPaneClick={() => select(null)}
      fitView
      // O padrão da biblioteca é só Backspace; quem tenta Delete concluiria que não dá para apagar.
      deleteKeyCode={['Delete', 'Backspace']}
      connectionLineType={ConnectionLineType.SmoothStep}
      // Raio de encaixe maior: acertar um conector de 13px no meio de uma linha é irritante.
      connectionRadius={30}
      defaultEdgeOptions={{ type: 'branch' }}
      proOptions={{ hideAttribution: false }}
    >
      <Background gap={18} size={1} color="#b6c2d1" />
      <Controls showInteractive={false} />
      <MiniMap pannable zoomable className="!bg-white" />
    </ReactFlow>
  );
}

/**
 * Área do canvas com o alvo de soltar. Fica separada do `<ReactFlow>` porque o drop precisa do
 * `screenToFlowPosition`, que só existe dentro do provider.
 */
function CanvasArea() {
  const addBlock = useEditor((state) => state.addBlock);
  const { screenToFlowPosition } = useReactFlow();

  return (
    <main
      className="min-w-0 flex-1 bg-slate-100"
      onDrop={(event) => {
        event.preventDefault();
        const type = event.dataTransfer.getData(BLOCK_DRAG_TYPE) as BlockKind;
        if (!type) return;
        addBlock(type, screenToFlowPosition({ x: event.clientX, y: event.clientY }));
      }}
      onDragOver={(event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
      }}
    >
      <Canvas />
    </main>
  );
}

/** Insere no centro da área visível, em cascata — antes ia sempre para (120,120) e empilhava. */
function PaletteWithInsert() {
  const addBlock = useEditor((state) => state.addBlock);
  const { screenToFlowPosition } = useReactFlow();
  const inserted = useRef(0);

  return (
    <Palette
      onInsert={(type) => {
        const offset = (inserted.current % 6) * 28;
        inserted.current += 1;
        const center = screenToFlowPosition({
          x: window.innerWidth / 2,
          y: window.innerHeight / 2,
        });
        addBlock(type, { x: center.x - 128 + offset, y: center.y - 60 + offset });
      }}
    />
  );
}

export function FlowPage() {
  return (
    <div className="flex min-h-0 flex-1">
      {/* Paleta e canvas dentro do mesmo provider: os dois precisam de `screenToFlowPosition`. */}
      <ReactFlowProvider>
        <PaletteWithInsert />
        <CanvasArea />
      </ReactFlowProvider>

      <section aria-label="Bloco selecionado" className="w-96 shrink-0 border-l border-slate-200">
        <Inspector />
      </section>
    </div>
  );
}
